import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Select from 'react-select';
import './RainSensorCountCorrectionModal.scss';
import {customStyles} from '../helpers/selectStyles';
import {
    getNearestRainSensors,
    updateRainSensorCounts
} from '../store/actions/sensorsActions';
import ButtonSpinner from './_common/ButtonSpinner';

const EMPTY_PERIOD = {
    dateTimeFrom: '',
    dateTimeTo: '',
    rainAmountMm: ''
};

const selector = state => {
    const {language} = state;
    return {
        t: language.translator
    };
};

const RainSensorCountCorrectionModal = props => {
    const {sensor, closeMethod, onSuccess} = props;
    const {t} = useSelector(selector);
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState('amount');
    const [rainAmountPeriods, setRainAmountPeriods] = useState([{...EMPTY_PERIOD}]);
    const [sourcePeriod, setSourcePeriod] = useState({dateTimeFrom: '', dateTimeTo: ''});
    const [sourceSensor, setSourceSensor] = useState(null);
    const [sourceSensorOptions, setSourceSensorOptions] = useState([]);
    const [sourceSensorsPending, setSourceSensorsPending] = useState(false);
    const [updatePending, setUpdatePending] = useState(false);

    useEffect(() => {
        let mounted = true;

        const loadSourceSensors = async () => {
            setSourceSensorsPending(true);
            const result = await dispatch(getNearestRainSensors(sensor.id));

            if (mounted && result && !result.error) {
                setSourceSensorOptions(result.map(item => ({
                    ...item,
                    label: item.name,
                    value: item.id
                })));
            }

            if (mounted) {
                setSourceSensorsPending(false);
            }
        };

        loadSourceSensors();

        return () => {
            mounted = false;
        };
    }, [dispatch, sensor.id]);

    const toUtcDateTime = dateTime => {
        if (!dateTime) {
            return '';
        }

        return new Date(dateTime).toISOString().slice(0, 19);
    };

    const isValidPeriod = period => {
        if (!period.dateTimeFrom || !period.dateTimeTo) {
            return false;
        }

        return new Date(period.dateTimeFrom).getTime() < new Date(period.dateTimeTo).getTime();
    };

    const isValidRainAmountPeriod = period => {
        return isValidPeriod(period) && Number(period.rainAmountMm) > 0;
    };

    const isRainAmountFormValid = rainAmountPeriods.length > 0
        && rainAmountPeriods.every(isValidRainAmountPeriod);

    const isSourceSensorFormValid = !!sourceSensor
        && isValidPeriod(sourcePeriod);

    const updateRainAmountPeriod = (index, field, value) => {
        setRainAmountPeriods(prev => prev.map((period, periodIndex) => (
            periodIndex === index
                ? {...period, [field]: value}
                : period
        )));
    };

    const addRainAmountPeriod = () => {
        setRainAmountPeriods(prev => [...prev, {...EMPTY_PERIOD}]);
    };

    const removeRainAmountPeriod = index => {
        setRainAmountPeriods(prev => prev.filter((period, periodIndex) => periodIndex !== index));
    };

    const buildPayload = () => {
        if (activeTab === 'amount') {
            return {
                targetSensorId: sensor.id,
                rainAmountCorrections: rainAmountPeriods.map(period => ({
                    dateTimeFrom: toUtcDateTime(period.dateTimeFrom),
                    dateTimeTo: toUtcDateTime(period.dateTimeTo),
                    rainAmountMm: Number(period.rainAmountMm)
                }))
            };
        }

        return {
            targetSensorId: sensor.id,
            sourceSensorCorrection: {
                sourceSensorId: sourceSensor.id,
                dateTimeFrom: toUtcDateTime(sourcePeriod.dateTimeFrom),
                dateTimeTo: toUtcDateTime(sourcePeriod.dateTimeTo)
            }
        };
    };

    const submitCorrection = async event => {
        event.preventDefault();

        if (updatePending) {
            return;
        }

        setUpdatePending(true);
        const result = await dispatch(updateRainSensorCounts(buildPayload()));
        setUpdatePending(false);

        if (result !== undefined && result !== null && !result.error) {
            closeMethod();
            onSuccess(result);
        }
    };

    return (
        <form className='rainSensorCorrectionModal' onSubmit={submitCorrection}>
            <div className='rainSensorCorrectionModalSensor'>
                <div>
                    <span>{t.gettext('Sensor Name')}</span>
                    <strong>{sensor.name}</strong>
                </div>
                <div>
                    <span>{t.gettext('Sensor Id')}</span>
                    <strong>{sensor.id}</strong>
                </div>
            </div>

            <div className='rainSensorCorrectionTabs'>
                <button
                    type='button'
                    className={activeTab === 'amount' ? 'rainSensorCorrectionTab active' : 'rainSensorCorrectionTab'}
                    onClick={() => setActiveTab('amount')}
                >
                    {t.gettext('Set period(s) and precipitation')}
                </button>
                <button
                    type='button'
                    className={activeTab === 'source' ? 'rainSensorCorrectionTab active' : 'rainSensorCorrectionTab'}
                    onClick={() => setActiveTab('source')}
                >
                    {t.gettext('Set source sensor and period')}
                </button>
            </div>

            {activeTab === 'amount' && (
                <div className='rainSensorCorrectionTabContent'>
                    {rainAmountPeriods.map((period, index) => (
                        <div className='rainSensorCorrectionPeriod' key={index}>
                            <div className='rainSensorCorrectionFields'>
                                <label className='rainSensorCorrectionField'>
                                    <span>{t.gettext('Date from')}</span>
                                    <input
                                        type='datetime-local'
                                        value={period.dateTimeFrom}
                                        onChange={event => updateRainAmountPeriod(index, 'dateTimeFrom', event.target.value)}
                                    />
                                </label>
                                <label className='rainSensorCorrectionField'>
                                    <span>{t.gettext('Date to')}</span>
                                    <input
                                        type='datetime-local'
                                        value={period.dateTimeTo}
                                        onChange={event => updateRainAmountPeriod(index, 'dateTimeTo', event.target.value)}
                                    />
                                </label>
                                <label className='rainSensorCorrectionField rainSensorCorrectionField--small'>
                                    <span>{t.gettext('Precipitation')}</span>
                                    <input
                                        type='number'
                                        min='0.01'
                                        step='0.01'
                                        value={period.rainAmountMm}
                                        onChange={event => updateRainAmountPeriod(index, 'rainAmountMm', event.target.value)}
                                    />
                                    <span className='rainSensorCorrectionUnit'>{t.gettext('mm')}</span>
                                </label>
                            </div>
                            {rainAmountPeriods.length > 1 && (
                                <button
                                    type='button'
                                    className='rainSensorCorrectionRemove'
                                    onClick={() => removeRainAmountPeriod(index)}
                                >
                                    {t.gettext('Remove period')}
                                </button>
                            )}
                        </div>
                    ))}

                    <button
                        type='button'
                        className='rainSensorCorrectionAdd'
                        onClick={addRainAmountPeriod}
                    >
                        {t.gettext('Add period')}
                    </button>
                </div>
            )}

            {activeTab === 'source' && (
                <div className='rainSensorCorrectionTabContent'>
                    <label className='rainSensorCorrectionSelectField'>
                        <span>{t.gettext('Source sensor')}</span>
                        <Select
                            options={sourceSensorOptions}
                            isDisabled={sourceSensorsPending}
                            isClearable={true}
                            maxMenuHeight={200}
                            placeholder={sourceSensorsPending ? t.gettext('Loading') : t.gettext('Select...')}
                            styles={customStyles}
                            onChange={setSourceSensor}
                            value={sourceSensor}
                            noOptionsMessage={() => t.gettext('No options')}
                        />
                    </label>
                    <div className='rainSensorCorrectionFields'>
                        <label className='rainSensorCorrectionField'>
                            <span>{t.gettext('Date from')}</span>
                            <input
                                type='datetime-local'
                                value={sourcePeriod.dateTimeFrom}
                                onChange={event => setSourcePeriod(prev => ({...prev, dateTimeFrom: event.target.value}))}
                            />
                        </label>
                        <label className='rainSensorCorrectionField'>
                            <span>{t.gettext('Date to')}</span>
                            <input
                                type='datetime-local'
                                value={sourcePeriod.dateTimeTo}
                                onChange={event => setSourcePeriod(prev => ({...prev, dateTimeTo: event.target.value}))}
                            />
                        </label>
                    </div>
                </div>
            )}

            <div className='rainSensorCorrectionActions'>
                <button
                    type='submit'
                    className='rainSensorCorrectionUpdate'
                    disabled={updatePending || (activeTab === 'amount' ? !isRainAmountFormValid : !isSourceSensorFormValid)}
                >
                    {t.gettext('Update counts')}
                    {updatePending && (
                        <ButtonSpinner/>
                    )}
                </button>
                <button
                    type='button'
                    className='rainSensorCorrectionCancel'
                    onClick={closeMethod}
                >
                    {t.gettext('Cancel')}
                </button>
            </div>
        </form>
    );
};

export default RainSensorCountCorrectionModal;
